import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/hooks/use-toast';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { buildNameLikeSelectOptions, buildUserSelectOptions } from '@/components/ui/multi-select-utils';
import { useClassTimeOptions } from '@/lib/api-call-hooks';
import {
  bulkUpdateStudentGroups,
  getRecreateSessionsSummary,
  getStudentGroup,
  getStudentGroups,
} from '@/lib/api-calls';
import { AllUpdateTeacherTypeValues } from '@/lib/coach-utils';
import { airServerErrorFrom } from '@/lib/error-handling';
import { formUpdateBulkActionsSchema } from '@/lib/student-group-utils';
import { distinct } from '@airreading/common';
import {
  CoachOverallGradeLabelMap,
  EditStudentGroupBulkActions,
  EditStudentGroupsBulkActionsOptions,
  type LessonUpdateSummary,
  type RecreateSessionsSummary,
  ScheduleFromStudentGroupBulkActions,
  StudentGroupWithScheduleAndLevel,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { BulkUpdateClassesForm } from './bulk-update-classes-form';
import { RecreateSessionSummaryModal } from './recreate-session-summary-modal';
import { UpdateLessonSummaryModal } from './update-lesson-summary-modal';

export interface BulkUpdateClassesProps {
  studentGroups: StudentGroupWithScheduleAndLevel[];
  trigger: ReactNode;
  bulkType: EditStudentGroupBulkActions;
  title: string;
  schoolId: string;
  timeZone: string | null;
  onSuccess?: () => void;
  isOneTimeClass?: boolean;
}

export const BulkUpdateClasses = ({
  studentGroups,
  trigger,
  bulkType,
  title,
  schoolId,
  timeZone,
  onSuccess,
  isOneTimeClass = false,
}: BulkUpdateClassesProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [errorModal, setErrorModal] = useState<string | null>(null);
  const [allStudents, setAllStudents] = useState<MultiSelectOption[]>([]);
  const [selectableCoaches, setSelectableCoaches] = useState<MultiSelectOption[]>([]);
  const [recreationSummaryData, setRecreationSummaryData] = useState<RecreateSessionsSummary | null>(null);
  const [showRecreationSummaryModal, setShowRecreationSummaryModal] = useState(false);
  const [lessonUpdateSummary, setLessonUpdateSummary] = useState<LessonUpdateSummary | null>(null);
  const [showLessonUpdateSummaryModal, setShowLessonUpdateSummaryModal] = useState(false);

  const { mutateAsync } = useMutation({
    mutationFn: bulkUpdateStudentGroups,
  });

  const { classTimeOptions } = useClassTimeOptions({ schoolId });

  useEffect(() => {
    if (bulkType === EditStudentGroupBulkActions.MergeClasses) {
      if (studentGroups.length <= 1) {
        setErrorModal('You must select at least two classes to merge. Please select more classes and try again.');
        return;
      }

      const allSameSchedule = studentGroups.map((sg) => sg.classTimeId).filter(distinct()).length === 1;

      if (!allSameSchedule) {
        setErrorModal('Please select classes with the same schedule and class times to merge.');
        return;
      }

      // Merge students and coaches from all selected student groups
      const studentIds = new Set<string>();
      const coachIds = new Set<string>();
      const uniqueStudents: MultiSelectOption[] = [];
      const uniqueCoaches: MultiSelectOption[] = [];

      for (const sg of studentGroups) {
        const { students, coach } = sg;

        for (const s of students ?? []) {
          if (!studentIds.has(s.id)) {
            studentIds.add(s.id);
            uniqueStudents.push(buildUserSelectOptions(s));
          }
        }

        if (coach && !coachIds.has(coach.id)) {
          const { id, fullNameOrEmail, coachProfile } = coach;
          const { overallGrade } = coachProfile;

          coachIds.add(id);
          uniqueCoaches.push({
            label: fullNameOrEmail,
            value: id,
            rightText: overallGrade ? CoachOverallGradeLabelMap[overallGrade] : '-',
          });
        }
      }

      setAllStudents(uniqueStudents);
      setSelectableCoaches(uniqueCoaches);
    } else if (bulkType === EditStudentGroupBulkActions.UpdateStudents) {
      const uniqueStudents = studentGroups
        .flatMap((sg) => sg.students ?? [])
        .filter(distinct((student) => student.id))
        .map(buildUserSelectOptions);

      setAllStudents(uniqueStudents);
    } else if (bulkType === EditStudentGroupBulkActions.RecreateSessions && studentGroups.length === 1) {
      setAllStudents((studentGroups[0].students ?? []).map(buildUserSelectOptions));
    }
  }, [bulkType, studentGroups]);

  const onSubmit = ({ formData, closeModal }: SubmitProps<z.infer<typeof formUpdateBulkActionsSchema>>) => {
    const {
      coach,
      learningPath,
      startingLesson,
      students,
      updateType,
      startPlainDate,
      endPlainDate,
      teacherType,
      coachAvailable,
      coachAll,
      schedule,
      classTime,
      reassigned,
      sendConfirmationEmail,
    } = formData;

    if (!timeZone) {
      toast({ title: 'Time Zone is required in District information' });
      return;
    }

    if (
      updateType === ScheduleFromStudentGroupBulkActions.WithRange &&
      (!startPlainDate || !endPlainDate || Temporal.PlainDate.compare(startPlainDate, endPlainDate) > 0)
    ) {
      toast({ title: 'Start date must be before end date' });
      return;
    }

    const studentGroupIds = studentGroups.map((sg) => sg.id);
    const duration = 15000;

    let data = {
      bulkType,
      startPlainDate,
      endPlainDate,
      studentGroupIds,
      timeZone,
    } as EditStudentGroupsBulkActionsOptions;

    switch (bulkType) {
      case EditStudentGroupBulkActions.ArchiveSessions: {
        const validStudentGroups = studentGroups.filter((sg) => sg.status !== 'archived');

        if (validStudentGroups.length === 0) {
          toast({ title: 'All selected classes are already archived' });
          return;
        }

        data = { ...data, bulkType, status: 'archived' };
        break;
      }
      case EditStudentGroupBulkActions.UpdateTeacher: {
        const selectedCoach = teacherType === AllUpdateTeacherTypeValues.Available ? coachAvailable : coachAll;
        const hasCoach = !!selectedCoach?.value;

        if (!hasCoach) {
          toast({ title: 'Please select a teacher' });
          return;
        }

        data = { ...data, bulkType, coachId: selectedCoach.value, reassigned };
        break;
      }
      case EditStudentGroupBulkActions.UpdateLesson: {
        const hasLesson = !!(learningPath?.value && startingLesson?.value);

        if (!hasLesson) {
          toast({ title: 'Please select a Learning path and Starting lesson' });
          return;
        }

        data = {
          ...data,
          bulkType,
          learningPathId: learningPath.value,
          startingLessonId: startingLesson.value,
        };
        break;
      }
      case EditStudentGroupBulkActions.UpdateStudents:
        if (!students || students.length === 0) {
          toast({ title: 'Please select at least one student' });
          return;
        }

        data = {
          ...data,
          bulkType,
          studentIds: students.map((student) => student.value),
        };
        break;
      case EditStudentGroupBulkActions.RecreateSessions: {
        const selectedCoach = teacherType === AllUpdateTeacherTypeValues.Available ? coachAvailable : coachAll;
        const hasCoach = !!selectedCoach?.value;
        const hasStudents = students && students.length > 0;
        const hasLesson = !!(learningPath?.value && startingLesson?.value);
        const hasLessonOneMissing =
          (!!learningPath?.value && !startingLesson?.value) || (!learningPath?.value && !!startingLesson?.value);

        if (hasLessonOneMissing) {
          toast({
            title: 'Missing fields',
            description: 'To update lesson, both Learning path and Starting lesson are required.',
            duration,
          });
          return;
        }

        const count = [hasCoach, hasStudents, hasLesson].filter(Boolean).length;
        const hasClassTime = !!(schedule?.value && classTime?.value);
        const isValid = (hasClassTime && count >= 0) || (!hasClassTime && count >= 2);

        if (!isValid) {
          if (!hasClassTime && count < 2) {
            toast({
              title: 'Please select at least two of the following',
              description: 'Teacher, Students, Learning path + Starting lesson',
              duration,
            });
            return;
          }

          toast({
            title: 'Invalid selection',
            description:
              'You can modify Schedule/Class time alone, Schedule/Class time with any of Teacher/Students/Learning path + Lesson, or (without Schedule/Class time) at least two of Teacher, Students, or Learning path + Lesson.',
            duration,
          });
          return;
        }

        data = {
          ...data,
          bulkType,
          ...(hasCoach ? { coachId: selectedCoach.value } : {}),
          ...(hasStudents ? { studentIds: students.map((student) => student.value) } : {}),
          ...(hasLesson ? { learningPathId: learningPath.value, startingLessonId: startingLesson.value } : {}),
          ...(hasClassTime ? { scheduleId: schedule.value, classTimeId: classTime.value } : {}),
        };

        getRecreateSessionsSummary(data)
          .then((summary) => setRecreationSummaryData(summary))
          .catch((e: unknown) => {
            const err = airServerErrorFrom(e);
            toast({
              title: err?.userFacingMessage ?? 'Failed to load recreate summary',
              variant: 'destructive',
            });
          });

        break;
      }
      case EditStudentGroupBulkActions.MergeClasses: {
        const hasCoach = !!coach?.value;
        const hasStudents = students && students.length > 0;
        const hasLesson = !!(learningPath?.value && startingLesson?.value);

        if (!hasCoach) {
          toast({ title: 'Please select a new teacher' });
          return;
        }

        if (!hasStudents) {
          toast({ title: 'Please select at least one student' });
          return;
        }

        if (!hasLesson) {
          toast({ title: 'Please select a Learning path and Starting lesson' });
          return;
        }

        data = {
          ...data,
          bulkType,
          coachId: coach.value,
          studentIds: students.map((student) => student.value),
          learningPathId: learningPath.value,
          startingLessonId: startingLesson.value,
        };
        break;
      }
      case EditStudentGroupBulkActions.RemoveFutureSessions: {
        data = { ...data, bulkType, sendConfirmationEmail };
        break;
      }
      case EditStudentGroupBulkActions.ExtendLastSession: {
        data = { ...data, bulkType, sendConfirmationEmail };
        break;
      }
    }

    return mutateAsync(data)
      .then((result) => {
        const { success, errors, lessonUpdateSummary: summary } = result;
        const hasLessonUpdateSummary =
          data.bulkType === EditStudentGroupBulkActions.UpdateLesson &&
          success > 0 &&
          ((summary?.fullyUpdated.length ?? 0) > 0 || (summary?.partiallyUpdated.length ?? 0) > 0);

        if (success > 0 && !hasLessonUpdateSummary) {
          const action = bulkType === EditStudentGroupBulkActions.MergeClasses ? 'merged' : 'updated';
          toast({ title: `${success} classes ${action}` });
        }

        if (errors && errors.length > 0) {
          toast({
            title: errors.length === 1 ? 'Error' : 'Errors',
            duration,
            description: (
              <ul>
                {errors.map((e: string) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ),
          });
        }

        if (success === 0 && (!errors || (errors && errors.length === 0))) {
          toast({ title: 'No classes were updated' });
        }

        queryClient.invalidateQueries(getStudentGroups());
        queryClient.invalidateQueries({ queryKey: ['AVAILABLE_COACHES'] });

        for (const id of studentGroupIds) {
          queryClient.invalidateQueries(getStudentGroup(id));
        }

        if (data.bulkType !== EditStudentGroupBulkActions.RecreateSessions && !hasLessonUpdateSummary) {
          onSuccess?.();
        }

        closeModal();

        if (data.bulkType === EditStudentGroupBulkActions.RecreateSessions && success > 0) {
          setShowRecreationSummaryModal(true);
        }

        if (hasLessonUpdateSummary && summary) {
          setLessonUpdateSummary(summary);
          setShowLessonUpdateSummaryModal(true);
        }
      })
      .catch((e: unknown) => {
        const error = airServerErrorFrom(e);
        toast({ title: error?.userFacingMessage || 'Failed to update classes', variant: 'destructive' });
      });
  };

  const startPlainDate = Temporal.Now.plainDateISO();
  const maxEndPlainDate = studentGroups
    .map((sg) => sg.overrideEndPlainDate ?? sg.classTime.schedule.endPlainDate)
    .filter(Boolean)
    .sort((a, b) => Temporal.PlainDate.compare(b, a))[0];

  const defaultValues: DefaultValues<z.infer<typeof formUpdateBulkActionsSchema>> = useMemo(() => {
    const base: DefaultValues<z.infer<typeof formUpdateBulkActionsSchema>> = {
      startPlainDate,
      endPlainDate: maxEndPlainDate,
      updateType: ScheduleFromStudentGroupBulkActions.FromNow,
      schedule: undefined,
      classTime: undefined,
      coach: undefined,
      learningPath: undefined,
      startingLesson: undefined,
      students: allStudents || [],
      teacherType: AllUpdateTeacherTypeValues.Available,
      reassigned: false,
      sendConfirmationEmail: false,
    };

    if (bulkType === EditStudentGroupBulkActions.RecreateSessions && studentGroups.length === 1) {
      const sg = studentGroups[0];
      const scheduleOption = sg.classTime?.schedule
        ? buildNameLikeSelectOptions({ id: sg.classTime.schedule.id, name: sg.classTime.schedule.name })
        : undefined;
      const classTimeOption =
        classTimeOptions?.find((o) => o.value === sg.classTime?.id) ??
        (sg.classTime ? { value: sg.classTime.id, label: '' } : undefined);
      const coachOption =
        sg.coach != null
          ? {
              label: sg.coach.fullNameOrEmail,
              value: sg.coach.id,
              rightText: sg.coach.coachProfile?.overallGrade
                ? CoachOverallGradeLabelMap[sg.coach.coachProfile.overallGrade]
                : '-',
            }
          : undefined;
      const studentsOptions = (sg.students ?? []).map(buildUserSelectOptions);
      const learningPathOption = sg.learningPath
        ? buildNameLikeSelectOptions({ id: sg.learningPath.id, name: sg.learningPath.name })
        : undefined;
      const startingLessonOption = sg.startingLesson
        ? buildNameLikeSelectOptions({ id: sg.startingLesson.id, name: sg.startingLesson.name })
        : undefined;
      return {
        ...base,
        updateType: ScheduleFromStudentGroupBulkActions.FromNow,
        teacherType: AllUpdateTeacherTypeValues.Available,
        schedule: scheduleOption,
        classTime: classTimeOption,
        coachAvailable: coachOption,
        coachAll: coachOption,
        students: studentsOptions,
        learningPath: learningPathOption,
        startingLesson: startingLessonOption,
      };
    }

    return base;
  }, [bulkType, studentGroups, startPlainDate, maxEndPlainDate, allStudents, classTimeOptions]);

  if (errorModal) {
    return (
      <Dialog>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
          </DialogHeader>
          {errorModal}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button">Confirm</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      {bulkType === EditStudentGroupBulkActions.RecreateSessions && recreationSummaryData && (
        <RecreateSessionSummaryModal
          open={showRecreationSummaryModal}
          onOpenChange={setShowRecreationSummaryModal}
          summary={recreationSummaryData}
          onClose={() => {
            setShowRecreationSummaryModal(false);
            setRecreationSummaryData(null);
            onSuccess?.();
          }}
        />
      )}
      {bulkType === EditStudentGroupBulkActions.UpdateLesson && lessonUpdateSummary && (
        <UpdateLessonSummaryModal
          open={showLessonUpdateSummaryModal}
          onOpenChange={setShowLessonUpdateSummaryModal}
          summary={lessonUpdateSummary}
          onClose={() => {
            setShowLessonUpdateSummaryModal(false);
            setLessonUpdateSummary(null);
            onSuccess?.();
          }}
        />
      )}
      <FormModal
        key={`bulk-${bulkType}-${allStudents.length}-${selectableCoaches.length}`}
        modalTitle={title}
        formSchema={formUpdateBulkActionsSchema}
        onSubmit={onSubmit}
        trigger={trigger}
        defaultValues={defaultValues}
        dialogContentProps={
          isOneTimeClass
            ? {
                className: 'max-w-lg lg:max-w-lg gap-3 p-5 sm:gap-3 sm:p-5 max-h-[90vh] overflow-visible',
              }
            : undefined
        }
        resetOnDefaultValuesChange={bulkType === EditStudentGroupBulkActions.UpdateStudents}
        footerLeadingContent={
          bulkType === EditStudentGroupBulkActions.RemoveFutureSessions ||
          bulkType === EditStudentGroupBulkActions.ExtendLastSession
            ? ({ form }) => (
                <FormCheckbox
                  horizontal
                  name="sendConfirmationEmail"
                  label="Send confirmation email"
                  form={form}
                  labelClassName="font-normal text-foreground"
                />
              )
            : undefined
        }
      >
        {({ form }) => {
          return (
            <BulkUpdateClassesForm
              form={form}
              bulkType={bulkType}
              schoolId={schoolId}
              selectableCoaches={selectableCoaches}
              studentGroups={studentGroups}
              startPlainDate={startPlainDate}
              endPlainDate={maxEndPlainDate}
              isOneTimeClass={isOneTimeClass}
            />
          );
        }}
      </FormModal>
    </>
  );
};
